import { type SxProps, type Theme } from '@mui/material';
import { DROPDOWN_OPTION_FONT_SIZE_SMALL } from '@onevision-resources/design-theme';
import fuzzy from 'fuzzy';
import { ArrowRight } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { sortBy } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  ModalContainer,
  ModalList,
  type ModalListFilter,
} from 'src/components/ModalContainer';
import { filterQuickActionPageGroups } from 'src/components/QuickActions/filter-quick-action-page-groups';
import {
  type QuickActionPage,
  type QuickActionPageTarget,
} from 'src/components/QuickActions/quick-action-pages.types';
import { useOpenQuickActionPage } from 'src/components/QuickActions/use-open-quick-action-page';
import { OptionCheck, OptionHint } from 'src/components/SelectMenu/select-menu.elements';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { QuickAction } from './quick-actions-context';

const UNGROUPED_ACTIONS_GROUP = 'Actions';

// after every action group, before Help & Support (MAX_SAFE_INTEGER) and Theme (Infinity)
const PAGES_ORDER = Number.MAX_SAFE_INTEGER - 1;

const fuzzyKeywordFilter: ModalListFilter = (_, search, keywords = []) =>
  fuzzy.filter(search, keywords).length > 0 ? 1 : 0;

const pageArrowSx: SxProps<Theme> = (theme) => ({
  paddingLeft: 0,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  opacity: 0.6,
  '& svg': { width: '0.875rem', height: '0.875rem' },
  '[cmdk-item][data-selected="true"] &': {
    color: theme.palette.custom.OV.sys.onBackground,
    opacity: 1,
  },
});

const toPageItem = (
  groupTitle: string,
  page: QuickActionPage,
  openPage: (target: QuickActionPageTarget) => void,
): QuickAction => ({
  id: page.id,
  title: page.title,
  group: groupTitle,
  order: PAGES_ORDER,
  icon: (
    <DynamicIcon
      name={page.iconData.name}
      library={page.iconData.library}
      filled={page.iconData.filled}
    />
  ),
  endAdornment: (
    <>
      {page.recordType ? (
        <OptionHint sx={{ fontSize: DROPDOWN_OPTION_FONT_SIZE_SMALL, lineHeight: '1.125rem' }}>
          {page.recordType}
        </OptionHint>
      ) : null}
      <OptionCheck aria-hidden sx={pageArrowSx}>
        <ArrowRight />
      </OptionCheck>
    </>
  ),
  handler: () => openPage(page.target),
});

export const QuickActionsModal: React.FC = () => {
  const { isOpened, modalProps } = useModalState(Modals.QuickActionsModal);
  const { closeModal } = useModalMethods(Modals.QuickActionsModal);
  const { showModal: showKeyboardShortcutsModal } = useModalMethods(Modals.ShortcutsModal);
  const { showModal: showContactSupportModal } = useModalMethods(Modals.ContactSupportModal);

  const { actions, pageGroups } = modalProps!;
  const [searchTerm, setSearchTerm] = useState('');
  const openPage = useOpenQuickActionPage();

  const onItemSelect = useCallback(
    (item: QuickAction) => {
      closeModal();
      item.handler?.();
    },
    [closeModal],
  );

  const openKeyboardShortcutsModal = useCallback(() => {
    showKeyboardShortcutsModal();
  }, [showKeyboardShortcutsModal]);

  const openContactSupportModal = useCallback(() => {
    showContactSupportModal();
  }, [showContactSupportModal]);

  const helpAndSupportActions = useMemo<QuickAction[]>(
    () => [
      {
        id: 'keyboard-shortcuts',
        title: 'Keyboard Shortcuts',
        description: 'Browse the list of available keyboard shortcuts.',
        icon: <DynamicIcon name="keyboard" library="lucide" />,
        handler: openKeyboardShortcutsModal,
        keywords: [
          'Keyboard Shortcuts',
          'Browse the list of available keyboard shortcuts.',
          'Help & Support',
        ],
        group: 'Help & Support',
        order: Number.MAX_SAFE_INTEGER,
      },
      {
        id: 'contact-support',
        title: 'Contact Support',
        description: 'Reach out to our support team for additional help.',
        icon: <DynamicIcon name="headphones" library="lucide" />,
        handler: openContactSupportModal,
        keywords: [
          'Contact Support',
          'Reach out to our support team for additional help.',
          'Help & Support',
        ],
        group: 'Help & Support',
        order: Number.MAX_SAFE_INTEGER,
      },
    ],
    [openKeyboardShortcutsModal, openContactSupportModal],
  );

  const pageItems = useMemo(
    () =>
      filterQuickActionPageGroups(pageGroups, searchTerm).flatMap((group) =>
        group.pages.map((page) => toPageItem(group.title, page, openPage)),
      ),
    [pageGroups, searchTerm, openPage],
  );

  // pages arrive already matched by their own word-start rules, which fuzzy matching would undo
  const filter = useMemo<ModalListFilter>(() => {
    const pageItemIds = new Set(pageItems.map((item) => item.id));
    return (id, search, keywords) =>
      pageItemIds.has(id) ? 1 : fuzzyKeywordFilter(id, search, keywords);
  }, [pageItems]);

  const orderedActions = useMemo(
    () =>
      sortBy(
        [...actions, ...helpAndSupportActions, ...pageItems],
        (action) => action.order ?? 999,
      ).map((action) => ({ ...action, group: action.group ?? UNGROUPED_ACTIONS_GROUP })),
    [actions, helpAndSupportActions, pageItems],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Quick actions"
      onClose={closeModal}
      data-testid="quick-actions-modal"
    >
      <ModalList
        items={orderedActions}
        onSelect={onItemSelect}
        onSearchChange={setSearchTerm}
        filter={filter}
        testId="quick-actions-modal"
      />
    </ModalContainer>
  );
};
